<template>
    <div class="title-and-controls">
        <span class="page-title">
            <div class="document-icon" v-html="fileIcon" />
            <EditableTitle v-model="artefact" icon="" />
            <span
                v-if="artefact?.revision"
                class="revision"
                :title="artefact.revision"
            >
                (Old version)
            </span>
        </span>
        <div class="controls">
            <Button
                v-for="button in buttons"
                :key="button.label"
                :class="button.class"
                :icon="button.icon"
                :label="button.label"
                @click="button.onClick"
            />
            <template v-if="artefact">
                <Button
                    severity="secondary"
                    icon="pi pi-ellipsis-v"
                    ref="optionsButtonElement"
                    @click="showMenu"
                />
                <ContextMenu ref="menu" :model="contextMenuItems" />
                <input
                    name="file"
                    type="file"
                    style="display: none"
                    ref="fileUploadElement"
                    @change="(e) => uploadOverwrite(e as HTMLInputEvent)"
                />
            </template>
        </div>
    </div>

    <div class="document" v-if="isDoneLoading">
        <template v-if="isViewingHistory">
            <template v-if="comparisonArtefact?.blob">
                <DiffViewer
                    v-if="selectedArtefact?.blob"
                    :from="comparisonArtefact.blob"
                    :to="selectedArtefact.blob"
                    viewType="history"
                    :mimetype="selectedArtefact.mimetype"
                    :diffFormat="
                        route.query.compare !== undefined
                            ? 'side-by-side'
                            : 'line-by-line'
                    "
                    :style="{ 'width': '100%' }"
                />
            </template>
        </template>
        <template v-else>
            <Spinner v-if="isFetching" />
            <FileViewer
                v-else-if="artefact"
                :artefact="artefact"
                :showToastOnSave="false"
            />
        </template>
    </div>
</template>

<script setup lang="ts">
import { useTaskManager } from "@/stores/task-store";
import {
    useArtefactManager,
    useArtefactsStore,
    withContent,
} from "@/lib/artefacts/user";
import { downloadBlob, mime } from "@/utils";
import { useOrganizationManager } from "@/composables/organization-manager";
import { useStekManager } from "@/composables/stek-manager";
import FileViewer from "@/components/FileViewer.vue";
import Spinner from "@/shared/Spinner.vue";
import EditableTitle from "@/components/EditableTitle.vue";
import { computed, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useCurrentEntityManager } from "@/stores/entity-store";
import { getIcon } from "material-file-icons";
import { useGatewayManager } from "@/stores/gateway-store";
import { useEventManager } from "@/stores/event-store";
import { useFlowManager } from "@/stores/flow-store";
import { useHistoryManager, useHistoryStore } from "@/stores/history";
import { useEditManager, useEditStore } from "@/composables/edit-manager";
import type { MenuItem } from "primevue/menuitem";
import ContextMenu from "primevue/contextmenu";
import Button, { ButtonProps } from "primevue/button";
import DiffViewer from "@/components/Diff/DiffViewer.vue";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { ContentFlavor, get_mimetype_flavor } from "@/lib/contentFlavor";

const menu = ref<typeof ContextMenu>();
const fileUploadElement = ref<HTMLElement>();

const optionsButtonElement = ref<typeof Button>();
const { artefact: artefactMeta } = useArtefactManager();
const { artefact, isFetching } = withContent(artefactMeta);
const { isEditing } = useEditManager();

const { loadHistory } = useHistoryStore();
const {
    isViewingLatestVersion,
    isViewingHistory,
    selectedArtefact,
    comparisonArtefact,
    isFetchingHistory,
    isFetchingHistoricalArtefact,
} = useHistoryManager();

const router = useRouter();
const route = useRoute();
const { organization } = useOrganizationManager();
const { stek } = useStekManager();
const { task } = useTaskManager();
const { gateway } = useGatewayManager();
const { event } = useEventManager();
const { flow } = useFlowManager();
const { domain, product, process, role } = useCurrentEntityManager();

const toast = useToast();

const isDoneLoading = computed(() => {
    return (
        !isFetching.value &&
        !isFetchingHistory.value &&
        !isFetchingHistoricalArtefact.value &&
        !isRestoring.value
    );
});

const artefactsStore = useArtefactsStore();
const isRestoring = ref(false);
const confirm = useConfirm();

const contentFlavor = computed(() => {
    return get_mimetype_flavor(artefact.value?.mimetype);
});

const { startEditing } = useEditStore();

const { updateArtefacts } = useArtefactsStore();

const download = () => {
    if (selectedArtefact.value) {
        const extension = mime.getExtension(selectedArtefact.value.mimetype);
        const blobUrl = URL.createObjectURL(selectedArtefact.value?.blob);
        downloadBlob(blobUrl, `${selectedArtefact.value.name}.${extension}`);
    }
};

const uploadOverwrite = async (event: HTMLInputEvent) => {
    const file_ = event.target.files?.[0];
    if (!file_) {
        toast.add({
            severity: "error",
            detail: "No file selected.",
        });
        return;
    }
    if (!artefact.value?.mimetype.includes(file_.type)) {
        toast.add({
            severity: "warn",
            detail: "You can only overwrite the current file with the same file type.",
        });
        return;
    }
    await artefactsStore.updateArtefactContent(artefact.value, file_, {
        showToast: false,
    });
    artefact.value.updateContent(file_);
    artefact.value.persist();
    startEditing();
};

const deleteConfirmation = async () => {
    return new Promise((resolve) => {
        confirm.require({
            header: "Delete",
            message: "Are you sure you want to delete this file?",
            icon: "pi pi-exclamation-triangle",
            rejectProps: {
                label: "Cancel",
                severity: "secondary",
                outlined: true,
            },
            acceptProps: {
                label: "Delete",
                severity: "danger",
            },
            defaultFocus: "reject",
            accept: () => {
                resolve(true);
            },
            reject: () => {
                resolve(false);
            },
            onHide: () => {
                resolve(false);
            },
        });
    });
};

const removedFile = async () => {
    if (!selectedArtefact.value) {
        return;
    }

    const shouldDelete = await deleteConfirmation();

    if (!shouldDelete) {
        return;
    }

    try {
        await artefactsStore.removeArtefact(selectedArtefact.value.identity);
    } catch {
        toast.add({
            severity: "warn",
            detail: `Could not remove file ${selectedArtefact.value?.name}`,
        });
        return;
    }

    await updateArtefacts();

    if (task.value) {
        router.push({
            name: "taskPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                taskId: task.value.identity,
            },
        });
    } else if (gateway.value) {
        router.push({
            name: "gatewayPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                gatewayId: gateway.value.identity,
            },
        });
    } else if (flow.value) {
        router.push({
            name: "flowPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                flowId: flow.value.identity,
            },
        });
    } else if (event.value) {
        router.push({
            name: "eventPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                eventId: event.value.identity,
            },
        });
    } else if (process.value) {
        router.push({
            name: "processPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                processId: process.value.identity,
            },
        });
    } else if (product.value) {
        router.push({
            name: "productPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                productId: product.value.identity,
            },
        });
    } else if (role.value) {
        router.push({
            name: "rolePage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                roleId: role.value.identity,
            },
        });
    } else if (domain.value) {
        router.push({
            name: "domainPage",
            params: {
                orgName: organization.value?.name,
                stekName: stek.value?.name,
                domainId: domain.value.identity,
            },
        });
    }
};
const restoreConfirmation = async () => {
    return new Promise((resolve) => {
        confirm.require({
            message: "Are you sure you want restore this version?",
            header: "Restore version",
            icon: "pi pi-exclamation-triangle",
            rejectProps: {
                label: "Cancel",
                severity: "secondary",
                outlined: true,
            },
            acceptProps: {
                label: "Restore",
            },
            accept: () => {
                resolve(true);
            },
            reject: () => {
                resolve(false);
            },
            onHide: () => {
                resolve(false);
            },
        });
    });
};
const buttonsAndTheirConditions = [
    {
        class: "p-button-secondary",
        icon: "pi pi-check",
        label: "Restore",
        click: async () => {
            const shouldRestore = await restoreConfirmation();
            if (!shouldRestore) {
                return;
            }

            isRestoring.value = true;
            try {
                await artefactsStore.restoreArtefact(selectedArtefact.value!);
                await Promise.all([updateArtefacts(), loadHistory()]);
                await router.push({
                    name: route.name,
                    params: {
                        ...route.params,
                        version: "current",
                    },
                    query: route.query,
                });
            } finally {
                isRestoring.value = false;
            }
        },
        condition: () => !isViewingLatestVersion.value,
    },
];

const buttons = computed<ButtonProps[]>(() => {
    return buttonsAndTheirConditions
        .filter((button) => !button.condition || button.condition())
        .map((button) => {
            return {
                class: button.class,
                icon: button.icon,
                label: button.label,
                onClick: button.click,
            };
        });
});

const showMenu = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    const rect = optionsButtonElement.value?.$el.getBoundingClientRect();
    // Fake the event that is consumed by 'show', to always position the context menu at
    // the bottom right of the clicked button vs where is clicked on the button.
    const fakeEvent = {
        pageX: rect.left + rect.width,
        pageY: rect.top + rect.height,
        stopPropagation(): void {},
        preventDefault(): void {},
    };
    menu.value?.show(fakeEvent);
};

const contextMenuItemsAndTheirConditions = [
    {
        label: "Download",
        icon: "pi pi-download",
        command: download,
        condition: () => contentFlavor.value !== ContentFlavor.GOOGLEDRIVE,
    },
    {
        label: "Upload",
        icon: "pi pi-upload",
        command: () => fileUploadElement.value?.click(),
        condition: () => isViewingLatestVersion.value,
    },
    {
        label: "Delete",
        icon: "pi pi-trash",
        command: removedFile,
        condition: () =>
            contentFlavor.value !== ContentFlavor.GOOGLEDRIVE &&
            isViewingLatestVersion.value,
    },

    {
        label: "History",
        icon: "pi pi-history",
        command: () => {
            router.push({
                name: route.name,
                params: {
                    ...route.params,
                    version: "current",
                },
            });
        },
        condition: () =>
            contentFlavor.value !== ContentFlavor.GOOGLEDRIVE &&
            !isViewingHistory.value &&
            !isEditing.value,
    },
];

const contextMenuItems = computed<MenuItem[]>(() => {
    return contextMenuItemsAndTheirConditions
        .filter((item) => !item.condition || item.condition())
        .map((item) => {
            return {
                label: item.label,
                icon: item.icon,
                command: item.command,
            };
        });
});

const fileIcon = computed(() => {
    if (!artefact.value) {
        return;
    }
    const filename = `.${mime.getExtension(artefact.value.mimetype)}`;
    return getIcon(filename).svg;
});
</script>

<style scoped lang="scss">
.page-title {
    margin-bottom: 0;
    display: flex;
    gap: 0.5rem;

    .document-icon {
        height: 3rem;
    }
    .revision {
        display: block;
        margin-top: 1.6rem;
        margin-left: 0.5rem;
        line-height: 1.6em;
        transform: translateY(-50%);
    }
}

.controls {
    display: flex;
    justify-content: flex-end;
    gap: 1rem;
    padding: 0.5rem 0;
    margin-bottom: 1rem;
}

.document {
    flex: 1;
    display: flex;
}
</style>
